/**
 * CRM REST client.
 *
 * A separate module from `api.ts` rather than another two hundred lines on the
 * end of it: the CRM is a distinct product behind the same origin, and one
 * import per screen keeps it obvious which surface a page is talking to. The
 * transport is shared -- `request` comes from `api.ts` -- so cookies, error
 * mapping and the `/api` prefix cannot drift apart.
 */

import { api, request } from "./api";
import type {
  ArchiveCandidate,
  ArchivedCompany,
  CompanyImportReport,
  AmCustomers,
  AmDashboard,
  AmScope,
  AmTeamSettings,
  AmThresholds,
  BranchDefaults,
  CompanyDetail,
  CompanyFilters,
  CompanyFollow,
  CompanyCredit,
  CompanyList,
  CompanySearchResult,
  CompanySuggestions,
  CompanySummaryResult,
  CompanyCreate,
  CompanyWrite,
  CreditPanel,
  CreditReportSummaryRow,
  CreditsafeSearchResponse,
  CreditsafeStatus,
  ContactDetail,
  BuiltinFieldRow,
  ContactWrite,
  ConversionPlan,
  CrmContact,
  CrmFieldType,
  CrmLaneBoards,
  CrmListView,
  CrmTeamListView,
  CustomFieldDefinition,
  CustomFieldValue,
  DomainMatches,
  DuplicateFlag,
  FieldSettings,
  FilterBar,
  FollowEvent,
  GroupRollup,
  OwnerCandidate,
  PendingAttachment,
  PicklistRow,
  QuickTask,
  ResolvedContactMethod,
  TaskRule,
  TaskSuggestions,
  TaskTrigger,
  RecordedOrder,
  CompanyPerformance,
  AvailabilityPanel,
  RecordedQuote,
  TimelinePage,
  WebhookDelivery,
  WebhookEndpoint,
  WebhookEventType,
  WebhookMode,
  WebhookSettings,
  WebhookTestResult,
} from "./crm-types";

export interface CompanyListParams {
  q?: string;
  ownerUserId?: number;
  unowned?: boolean;
  lifecycleStage?: string;
  customerType?: string;
  leadPriority?: string;
  parentCompanyId?: number;
  /** Only companies that could be a group, for the "which group?" picker. */
  canBeGroup?: boolean;
  /**
   * Any column key, or the legacy `name` / `recent` / `created`.
   *
   * Widened from the three legacy values when every heading became clickable.
   * The server resolves it and falls back to name for anything it does not
   * know, so a bookmarked link naming a since-deleted column still renders.
   */
  sort?: string;
  dir?: "asc" | "desc";
  /** Ad-hoc filters by field key, serialised as `f_<key>` in the query. */
  filters?: Record<string, string>;
  limit?: number;
  offset?: number;
}

/** Drops empty values so the URL stays readable and cache keys stay stable. */
function qs(params: Record<string, string | number | boolean | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === "" || value === false) continue;
    search.set(key, String(value));
  }
  const query = search.toString();
  return query ? `?${query}` : "";
}

const json = (body: unknown): RequestInit => ({
  method: "POST",
  body: JSON.stringify(body),
});

export const crmApi = {
  // ── Companies ─────────────────────────────────────────────────────────────

  companies: (params: CompanyListParams = {}) => {
    const { filters, ...rest } = params;

    /*
     * Ad-hoc filters go out as `f_<key>`, matching `adhocFilters` on the
     * server. Prefixed so a field named after a real parameter -- `f_sort` for
     * a field called sort -- cannot collide with the sort itself.
     */
    const flat: Record<string, string | number | boolean | undefined> = { ...rest };
    for (const [key, value] of Object.entries(filters ?? {})) {
      flat[`f_${key}`] = value;
    }

    return request<CompanyList>(`/crm/companies${qs(flat)}`);
  },

  /** Which filters this person keeps in their bar, and what they could add. */
  filterBar: (viewKey = "companies") =>
    request<FilterBar>(`/crm/views/${viewKey}/filters`),

  saveFilterBar: (filters: string[], viewKey = "companies") =>
    request<FilterBar>(`/crm/views/${viewKey}/filters`, {
      method: "PUT",
      body: JSON.stringify({ filters }),
    }),

  resetFilterBar: (viewKey = "companies") =>
    request<FilterBar>(`/crm/views/${viewKey}/filters`, { method: "DELETE" }),

  filters: () => request<CompanyFilters>("/crm/companies/filters"),

  /**
   * The column layout in force for whoever is asking.
   *
   * Resolved server-side through personal, team and desk -- so the list simply
   * renders `columns` and does not have to know there are layers. The layers
   * come back alongside for the editor.
   */
  listView: (viewKey = "companies") =>
    request<CrmListView>(`/crm/views/${viewKey}`),

  /** Admin only: the desk-wide layout, under every team's. */
  saveListView: (columns: string[], viewKey = "companies") =>
    request<CrmListView>(`/crm/views/${viewKey}`, {
      method: "PUT",
      body: JSON.stringify({ columns }),
    }),

  /** This person's own columns. Theirs to set, like their filter bar. */
  saveMyListView: (columns: string[], viewKey = "companies") =>
    request<CrmListView>(`/crm/views/${viewKey}/mine`, {
      method: "PUT",
      body: JSON.stringify({ columns }),
    }),

  /** Back to this person's team's layout, or the desk's. */
  resetMyListView: (viewKey = "companies") =>
    request<CrmListView>(`/crm/views/${viewKey}/mine`, { method: "DELETE" }),

  /** Every team and the layout it has. Readable by anybody. */
  teamListViews: (viewKey = "companies") =>
    request<CrmTeamListView[]>(`/crm/views/${viewKey}/teams`),

  /** Admin only: changes what one team sees. */
  saveTeamListView: (teamId: number, columns: string[], viewKey = "companies") =>
    request<CrmTeamListView[]>(`/crm/views/${viewKey}/teams/${teamId}`, {
      method: "PUT",
      body: JSON.stringify({ columns }),
    }),

  /** Admin only: hand a team back to the desk's layout. */
  resetTeamListView: (teamId: number, viewKey = "companies") =>
    request<CrmTeamListView[]>(`/crm/views/${viewKey}/teams/${teamId}`, {
      method: "DELETE",
    }),

  /**
   * Star an unowned account as needing a manager, or take the star off.
   *
   * The end state rather than a toggle: two people looking at the same unowned
   * account both pressing the star must not undo each other.
   *
   * 409 when the account already has a manager -- the message names them,
   * which is usually what the person starring it wanted to know.
   */
  setOpportunity: (
    id: number,
    input: { starred: boolean; note?: string | null },
  ) =>
    request<CompanyDetail>(`/crm/companies/${id}/opportunity`, {
      method: "POST",
      body: JSON.stringify(input),
    }),

  /** Who already uses this domain, asked while a new company is being typed. */
  byDomain: (domain: string, name = "") =>
    request<DomainMatches>(`/crm/companies/by-domain${qs({ domain, name })}`),

  /**
   * Everything derivable from a domain, for the new-company form.
   *
   * Separate from `byDomain`, which only answers "who already uses this" and
   * has to stay instant on every keystroke -- this one may reach a bureau.
   */
  suggest: (domain: string, name = "") =>
    request<CompanySuggestions>(`/crm/companies/suggest${qs({ domain, name })}`),

  /** What a new branch inherits from its group. */
  branchDefaults: (parentId: number) =>
    request<BranchDefaults>(`/crm/companies/${parentId}/branch-defaults`),

  company: (id: number) => request<CompanyDetail>(`/crm/companies/${id}`),

  createCompany: (input: CompanyCreate) =>
    request<CompanyDetail>("/crm/companies", json(input)),

  /**
   * Create companies from a spreadsheet, or preview what that would do.
   *
   * `apply: false` runs the whole thing -- parse, conflict resolution, report
   * -- and stops before writing, so the preview is a preview of the import
   * rather than of something that resembles it.
   */
  importCompanies: (csv: string, apply: boolean) =>
    request<CompanyImportReport>("/crm/companies/import", json({ csv, apply })),

  updateCompany: (id: number, patch: Partial<CompanyWrite>) =>
    request<CompanyDetail>(`/crm/companies/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),

  /** Branch count, people and open tickets across a group. */
  rollup: (id: number) =>
    request<{ rollup: GroupRollup | null }>(`/crm/companies/${id}/rollup`),

  ownerSuggestion: (id: number) =>
    request<{ candidates: OwnerCandidate[] }>(
      `/crm/companies/${id}/owner-suggestion`,
    ),

  setCustomField: (id: number, fieldId: number, value: string | null) =>
    request<{ customFields: CustomFieldValue[] }>(
      `/crm/companies/${id}/custom-fields`,
      json({ fieldId, value }),
    ),

  generateSummary: (id: number) =>
    request<CompanySummaryResult>(`/crm/companies/${id}/summary`, {
      method: "POST",
    }),

  // ── Contacts ──────────────────────────────────────────────────────────────

  contacts: (companyId: number) =>
    request<CrmContact[]>(`/crm/companies/${companyId}/contacts`),

  createContact: (companyId: number, input: ContactWrite & { email: string }) =>
    request<CrmContact>(`/crm/companies/${companyId}/contacts`, json(input)),

  updateContact: (id: number, patch: ContactWrite) =>
    request<CrmContact>(`/crm/contacts/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),

  /**
   * File a contact, or move one who is at the wrong company.
   *
   * `withHistory` is required because there is no safe default: their calls
   * and notes were the old account's if they have changed employer, and were
   * never the old account's if they were simply filed wrongly. Their tickets
   * follow them either way.
   */
  moveContact: (id: number, companyId: number, withHistory: boolean) =>
    request<CrmContact>(
      `/crm/contacts/${id}/move`,
      json({ companyId, withHistory }),
    ),

  /** Detaches rather than deletes -- the row is what tickets point at. */
  detachContact: (id: number) =>
    request<{ ok: true }>(`/crm/contacts/${id}`, { method: "DELETE" }),

  contactMethod: (id: number) =>
    request<ResolvedContactMethod>(`/crm/contacts/${id}/preferred-method`),

  /** One person and their company, for a caller holding only the id. */
  contact: (id: number) => request<ContactDetail>(`/crm/contacts/${id}`),

  // ── Timeline ──────────────────────────────────────────────────────────────

  timeline: (
    companyId: number,
    params: {
      kinds?: string[];
      cursor?: string | null;
      limit?: number;
      includeBranches?: boolean;
    } = {},
  ) =>
    request<TimelinePage>(
      `/crm/companies/${companyId}/timeline${qs({
        kinds: params.kinds?.length ? params.kinds.join(",") : undefined,
        cursor: params.cursor ?? undefined,
        limit: params.limit,
        includeBranches: params.includeBranches ? "true" : undefined,
      })}`,
    ),

  /** The same merge scoped to one person, for the Helpdesk's contact drawer. */
  contactTimeline: (
    contactId: number,
    params: { kinds?: string[]; cursor?: string | null; limit?: number } = {},
  ) =>
    request<TimelinePage>(
      `/crm/contacts/${contactId}/timeline${qs({
        kinds: params.kinds?.length ? params.kinds.join(",") : undefined,
        cursor: params.cursor ?? undefined,
        limit: params.limit,
      })}`,
    ),

  addNote: (
    companyId: number,
    body: string,
    contactId?: number | null,
    attachments?: PendingAttachment[],
  ) =>
    request<{ id: number }>(
      `/crm/companies/${companyId}/notes`,
      json({ body, contactId: contactId ?? null, attachments }),
    ),

  logCall: (
    companyId: number,
    input: {
      direction: "inbound" | "outbound";
      contactId?: number | null;
      subject?: string;
      body?: string;
      durationMinutes?: number;
      occurredAt?: string;
      attachments?: PendingAttachment[];
    },
  ) => request<{ id: number }>(`/crm/companies/${companyId}/calls`, json(input)),

  logMeeting: (
    companyId: number,
    input: {
      subject: string;
      body?: string;
      contactId?: number | null;
      startsAt: string;
      endsAt?: string;
      location?: string;
      attachments?: PendingAttachment[];
    },
  ) => request<{ id: number }>(`/crm/companies/${companyId}/meetings`, json(input)),

  /**
   * A WhatsApp message or an email that never came through Pulse.
   *
   * One call for both: same shape, and they are only separate activity types so
   * the timeline can be filtered to either.
   */
  logMessage: (
    companyId: number,
    input: {
      type: "whatsapp" | "email";
      direction: "inbound" | "outbound";
      contactId?: number | null;
      subject?: string;
      body?: string;
      occurredAt?: string;
      attachments?: PendingAttachment[];
    },
  ) => request<{ id: number }>(`/crm/companies/${companyId}/messages`, json(input)),

  /** A ticket handled somewhere other than Pulse -- a supplier's, say. */
  logExternalTicket: (
    companyId: number,
    input: {
      subject: string;
      reference?: string;
      body?: string;
      contactId?: number | null;
      occurredAt?: string;
      attachments?: PendingAttachment[];
    },
  ) =>
    request<{ id: number }>(
      `/crm/companies/${companyId}/external-tickets`,
      json(input),
    ),

  addReference: (
    companyId: number,
    input: {
      type: "quote" | "order";
      reference: string;
      outcome?: "won" | "lost" | "open";
      /** Net of VAT, in pounds -- the same unit Team reports in. */
      netTotal?: number;
      occurredAt?: string;
      body?: string;
      contactId?: number | null;
      attachments?: PendingAttachment[];
    },
  ) =>
    request<{ id: number }>(`/crm/companies/${companyId}/references`, json(input)),

  /** Only the sales boards, with this agent's own team preselected. */
  laneBoards: (companyId?: number) =>
    request<CrmLaneBoards>(`/crm/lane/boards${qs({ companyId })}`),

  createTask: (
    companyId: number,
    input: {
      title: string;
      description?: string;
      /** Omit to file it wherever the acting agent's sales team's work goes. */
      boardId?: string;
      statusId?: string;
      /** Omit for the account manager, or yourself when they do not qualify. */
      assigneeIds?: string[];
      dueAt?: string;
      contactId?: number | null;
    },
  ) => request<{ ok: true }>(`/crm/companies/${companyId}/tasks`, json(input)),

  /**
   * Correct an entry you wrote. Only the words; the server refuses after the
   * edit window and for anything somebody else typed.
   */
  editActivity: (
    id: number,
    patch: { subject?: string | null; body?: string | null },
  ) =>
    request<{ ok: true }>(`/crm/activities/${id}`, {
      ...json(patch),
      method: "PATCH",
    }),

  // ── Attachments ───────────────────────────────────────────────────────────

  /**
   * Put the bytes up, before there is an entry to hang them off.
   *
   * The helpdesk's own endpoint, unchanged: it writes to `pending/` and returns
   * a key, and knows nothing about what will own it. Sharing it is the point --
   * one upload path means one size limit and one place bytes can enter.
   */
  uploadAttachment: (file: File) => api.upload(file),

  /**
   * Where the browser fetches one from.
   *
   * Images are served inline and everything else downloads; the server decides
   * which from the stored type, so this is the same URL either way. `download`
   * asks for a picture to be saved rather than shown, and can only make the
   * response stricter.
   */
  attachmentUrl: (id: number, options: { download?: boolean } = {}) =>
    `/api/crm/attachments/${id}${options.download ? "?download=1" : ""}`,

  /** Take a file off an entry. Allowed while the entry is still editable. */
  deleteAttachment: (id: number) =>
    request<{ ok: true }>(`/crm/attachments/${id}`, { method: "DELETE" }),

  // ── Search ────────────────────────────────────────────────────────────────

  search: (q: string) =>
    request<{ results: CompanySearchResult[] }>(`/crm/search${qs({ q })}`),

  // ── Duplicates ────────────────────────────────────────────────────────────

  duplicates: (status: "open" | "dismissed" | "merged" = "open") =>
    request<{ duplicates: DuplicateFlag[] }>(`/crm/duplicates${qs({ status })}`),

  dismissDuplicate: (id: number) =>
    request<{ ok: true }>(`/crm/duplicates/${id}/dismiss`, { method: "POST" }),

  /** Admin only. Irreversible from the UI, hence the explicit target. */
  mergeCompanies: (sourceCompanyId: number, targetCompanyId: number) =>
    request<{ ok: true; companyId: number }>(
      "/crm/merge",
      json({ sourceCompanyId, targetCompanyId }),
    ),

  rescanDuplicates: () =>
    request<{ scanned: number; found: number }>("/crm/duplicates/rescan", {
      method: "POST",
    }),

  /**
   * Admin only. Takes the company out of the CRM and returns its people to the
   * unfiled queue; `restoreCompany` undoes both.
   */
  archiveCompany: (id: number) =>
    request<{ ok: true; unfiledContacts: number }>(`/crm/companies/${id}/archive`, {
      method: "POST",
    }),

  restoreCompany: (id: number) =>
    request<{ ok: true; refiledContacts: number }>(`/crm/companies/${id}/restore`, {
      method: "POST",
    }),

  archivedCompanies: () => request<ArchivedCompany[]>("/crm/archive"),

  /** What the tidy-up would archive with this look-back. Admin only. */
  archiveCandidates: (months: number) =>
    request<{ total: number; rows: ArchiveCandidate[] }>(
      `/crm/archive/candidates?months=${months}`,
    ),

  bulkArchive: (companyIds: number[], months: number) =>
    request<{ archived: number; skipped: number }>(
      "/crm/archive/bulk",
      json({ companyIds, months }),
    ),

  /**
   * "Not the same company — the same group."
   *
   * Open to any agent, unlike merging: setting a parent is undone by clearing
   * it. Pass `parentCompanyId` to put the others under a company that already
   * exists, or `parentName` to create the group record itself.
   */
  groupCompanies: (input: {
    companyIds: number[];
    parentCompanyId?: number | null;
    parentName?: string;
  }) =>
    request<{
      parentCompanyId: number;
      /** Companies filed under the group. */
      linked: number;
      /** Branches that came up a level with a joiner that was itself a group. */
      absorbed: number;
    }>("/crm/companies/group", json(input)),

  // ── Manager settings ──────────────────────────────────────────────────────

  fields: () => request<CustomFieldDefinition[]>("/crm/fields"),

  createField: (input: {
    label: string;
    type: "text" | "number" | "dropdown" | "date";
    options?: string[];
    helpText?: string | null;
  }) => request<CustomFieldDefinition>("/crm/fields", json(input)),

  updateField: (
    id: number,
    patch: {
      label?: string;
      options?: string[];
      helpText?: string | null;
      position?: number;
      isActive?: boolean;
    },
  ) =>
    request<CustomFieldDefinition>(`/crm/fields/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),

  /** Built-ins, custom fields, deleted ones and the option lists, together. */
  fieldSettings: () => request<FieldSettings>("/crm/field-settings"),

  /**
   * What changing this field's type would convert and what it would clear.
   *
   * Always called before `changeFieldType`, which refuses a casualty count it
   * disagrees with -- so this is not an optional preview, it is the first half
   * of the operation.
   */
  previewFieldType: (id: number, type: CrmFieldType, options: string[] = []) =>
    request<ConversionPlan>(
      `/crm/fields/${id}/type/preview`,
      json({ type, options }),
    ),

  changeFieldType: (
    id: number,
    type: CrmFieldType,
    options: string[],
    expectedCasualties: number,
  ) =>
    request<CustomFieldDefinition>(
      `/crm/fields/${id}/type`,
      json({ type, options, expectedCasualties }),
    ),

  /**
   * Gone for good. The values are archived server-side and readable only from
   * a SQL prompt -- there is deliberately no route that reads them back.
   *
   * Answers with the definition as it was, so a caller can name what it just
   * removed.
   */
  deleteField: (id: number) =>
    request<CustomFieldDefinition>(`/crm/fields/${id}`, { method: "DELETE" }),

  /** Rename, hide or reposition a field that is a column on `companies`. */
  updateBuiltinField: (
    key: string,
    patch: { label?: string | null; isActive?: boolean; position?: number | null },
  ) =>
    request<BuiltinFieldRow[]>(`/crm/builtin-fields/${encodeURIComponent(key)}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),

  /** The whole order in one write -- see the note on `reorderBuiltins`. */
  reorderBuiltinFields: (keys: string[]) =>
    request<BuiltinFieldRow[]>("/crm/builtin-fields/reorder", json({ keys })),

  // ── Following an account ──────────────────────────────────────────────────

  companyFollow: (companyId: number) =>
    request<CompanyFollow>(`/crm/companies/${companyId}/follow`),

  /** The whole set at once; an empty array unfollows. */
  setCompanyFollow: (companyId: number, events: FollowEvent[]) =>
    request<{ events: FollowEvent[] }>(`/crm/companies/${companyId}/follow`, {
      method: "PUT",
      body: JSON.stringify({ events }),
    }),

  // ── Quick tasks and suggestion rules ──────────────────────────────────────

  quickTasks: () => request<QuickTask[]>("/crm/quick-tasks"),

  createQuickTask: (input: {
    label: string;
    description?: string | null;
    dueInDays?: number | null;
  }) => request<QuickTask>("/crm/quick-tasks", json(input)),

  updateQuickTask: (
    id: number,
    patch: {
      label?: string;
      description?: string | null;
      dueInDays?: number | null;
      position?: number;
    },
  ) =>
    request<QuickTask>(`/crm/quick-tasks/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),

  /** Really deletes. Nothing points at a button, so nothing is orphaned. */
  deleteQuickTask: (id: number) =>
    request<{ ok: true }>(`/crm/quick-tasks/${id}`, { method: "DELETE" }),

  taskRules: () => request<TaskRule[]>("/crm/task-rules"),

  createTaskRule: (input: {
    name: string;
    trigger: TaskTrigger;
    thresholdDays: number;
    lifecycleStage?: string | null;
    taskTitle: string;
    taskDescription?: string | null;
  }) => request<TaskRule>("/crm/task-rules", json(input)),

  updateTaskRule: (
    id: number,
    patch: Partial<{
      name: string;
      trigger: TaskTrigger;
      thresholdDays: number;
      lifecycleStage: string | null;
      taskTitle: string;
      taskDescription: string | null;
      isActive: boolean;
    }>,
  ) =>
    request<TaskRule>(`/crm/task-rules/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),

  deleteTaskRule: (id: number) =>
    request<{ ok: true }>(`/crm/task-rules/${id}`, { method: "DELETE" }),

  /** Evaluated live against this company, not read from a swept table. */
  taskSuggestions: (companyId: number) =>
    request<TaskSuggestions>(`/crm/companies/${companyId}/task-suggestions`),

  dismissTaskSuggestion: (companyId: number, ruleId: number) =>
    request<{ ok: true }>(
      `/crm/companies/${companyId}/task-suggestions/${ruleId}/dismiss`,
      { method: "POST" },
    ),

  picklists: () => request<Record<string, PicklistRow[]>>("/crm/picklists"),

  addPicklistOption: (input: { listKey: string; label: string }) =>
    request<{ id: number; value: string; reactivated: boolean }>(
      "/crm/picklists",
      json(input),
    ),

  updatePicklistOption: (
    id: number,
    patch: { label?: string; position?: number; isActive?: boolean },
  ) =>
    request<PicklistRow>(`/crm/picklists/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),

  // ── Creditsafe ────────────────────────────────────────────────────────────

  /** Whether a bureau is connected, and which countries it covers. */
  creditsafeStatus: () => request<CreditsafeStatus>("/crm/creditsafe/status"),

  /**
   * Find a company at Creditsafe. Free -- unlike ordering a report.
   *
   * Not under a company id, because it is also how somebody checks a prospect
   * nobody has created a record for yet.
   */
  creditsafeSearch: (params: {
    name?: string;
    regNo?: string;
    vatNo?: string;
    postCode?: string;
    city?: string;
    website?: string;
    countries?: string;
    exact?: boolean;
    page?: number;
    pageSize?: number;
  }) =>
    request<CreditsafeSearchResponse>(`/crm/creditsafe/search${qs({ ...params })}`),

  /** The stored credit file. Reads only; this never orders anything. */
  credit: (companyId: number) =>
    request<CreditPanel<CompanyCredit>>(`/crm/companies/${companyId}/credit`),

  /** Every report ordered here, newest first. */
  creditHistory: (companyId: number) =>
    request<{ reports: CreditReportSummaryRow[] }>(
      `/crm/companies/${companyId}/credit/history`,
    ),

  /**
   * Link a company to a bureau record and order its first report.
   *
   * **Billed.** `safeNo` is carried from the search hit rather than derived, so
   * the Safe Number on screen can never be a registration number instead.
   */
  linkCredit: (companyId: number, link: { connectId: string; safeNo: string | null }) =>
    request<CreditPanel<CompanyCredit>>(
      `/crm/companies/${companyId}/credit/link`,
      json(link),
    ),

  /** Order a fresh report for an already-linked company. **Billed.** */
  refreshCredit: (companyId: number) =>
    request<CreditPanel<CompanyCredit>>(
      `/crm/companies/${companyId}/credit/refresh`,
      { method: "POST" },
    ),

  /** Break the link and clear the cached figures. Stored reports survive. */
  unlinkCredit: (companyId: number) =>
    request<CreditPanel<CompanyCredit>>(`/crm/companies/${companyId}/credit`, {
      method: "DELETE",
    }),

  // ── Quotes and orders ─────────────────────────────────────────────────────
  //
  // Read from the company's timeline, where they are recorded. `teamStatus`
  // and `syncTeam` were here and are gone: there is no API into Team to be
  // reachable or to pull account managers from, so neither question exists.

  quotes: (companyId: number) =>
    request<AvailabilityPanel<RecordedQuote[]>>(`/crm/companies/${companyId}/quotes`),

  orders: (companyId: number) =>
    request<AvailabilityPanel<RecordedOrder[]>>(`/crm/companies/${companyId}/orders`),

  /** Revenue and gross profit. Admins only -- anybody else gets a 403. */
  performance: (companyId: number) =>
    request<CompanyPerformance>(`/crm/companies/${companyId}/performance`),

  // ── Webhooks (admin only) ─────────────────────────────────────────────────

  webhooks: () => request<WebhookSettings>("/crm/webhooks"),

  createWebhook: (input: {
    name: string;
    mode: WebhookMode;
    url: string | null;
    events: WebhookEventType[];
  }) => request<WebhookEndpoint>("/crm/webhooks", json(input)),

  updateWebhook: (
    id: number,
    patch: {
      name?: string;
      url?: string;
      events?: WebhookEventType[];
      isActive?: boolean;
    },
  ) =>
    request<WebhookEndpoint>(`/crm/webhooks/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),

  deleteWebhook: (id: number) =>
    request<void>(`/crm/webhooks/${id}`, { method: "DELETE" }),

  /** Invalidates every signature the receiver currently accepts. */
  rotateWebhookSecret: (id: number) =>
    request<WebhookEndpoint>(`/crm/webhooks/${id}/rotate-secret`, {
      method: "POST",
    }),

  webhookDeliveries: (id: number, limit = 20) =>
    request<WebhookDelivery[]>(`/crm/webhooks/${id}/deliveries${qs({ limit })}`),

  /** Posts a signed sample now. Records nothing against the endpoint. */
  testWebhook: (id: number) =>
    request<WebhookTestResult>(`/crm/webhooks/${id}/test`, { method: "POST" }),

  /** Batch and send whatever is waiting, rather than waiting for the timer. */
  flushWebhooks: () =>
    request<{
      batched: number;
      delivered: number;
      failed: number;
      retrying: number;
    }>("/crm/webhooks/flush", { method: "POST" }),

  // ── Account manager dashboard ──────────────────────────────────────────────

  /** Which tabs this person gets. The server decides; never infer it here. */
  amScopes: () => request<{ scopes: AmScope[] }>("/crm/am-dashboard/scopes"),

  amDashboard: (scope: string) =>
    request<AmDashboard>(`/crm/am-dashboard${qs({ scope })}`),

  amCustomers: (
    scope: string,
    filter?: "at-risk" | "open-quotes" | "quiet",
    sort?: "risk" | "name" | "contact" | "spend",
    /** An account manager's id as a string, or "none" for the unmanaged. */
    owner?: string,
  ) =>
    request<AmCustomers>(
      `/crm/am-dashboard/customers${qs({ scope, filter, sort, owner })}`,
    ),

  amSettings: () =>
    request<{ settings: AmTeamSettings[] }>("/crm/am-dashboard/settings"),

  /** Admin only: the at-risk rules one sales desk works to. */
  saveAmSettings: (teamSlug: string, patch: Partial<AmThresholds>) =>
    request<{ settings: AmTeamSettings[] }>(
      `/crm/am-dashboard/settings/${encodeURIComponent(teamSlug)}`,
      { method: "PUT", body: JSON.stringify(patch) },
    ),
};
